import * as Sentry from '@sentry/react';
import {
  clearStaleBuildReloadGuard,
  extractAssetUrl,
  isStaleBuildError,
  recoverFromStaleBuild,
} from 'app/utils/staleBuild.utils';
import i18n from 'i18n';
import React from 'react';

type AppErrorBoundaryProps = {
  children: React.ReactNode;
};

type AppErrorBoundaryState = {
  error: Error | null;
  isStaleBuild: boolean;
};

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, isStaleBuild: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error, isStaleBuild: isStaleBuildError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const staleBuild = isStaleBuildError(error);

    Sentry.withScope(scope => {
      scope.setTag('stale_build', String(staleBuild));
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });

    if (staleBuild) {
      recoverFromStaleBuild(extractAssetUrl(error));
    }
  }

  // A plain reload would re-read the cached failure, so go through the same eviction path.
  handleReload = (): void => {
    clearStaleBuildReloadGuard();
    recoverFromStaleBuild();
  };

  render(): React.ReactNode {
    const { error, isStaleBuild } = this.state;

    if (!error) {
      return this.props.children;
    }

    const messageKey = isStaleBuild ? 'errorBoundary.staleBuild' : 'errorBoundary.unexpected';

    return (
      <div className="flex flex-col items-center justify-center p-24 text-center" style={{ minHeight: 320 }}>
        <h2 className="text-20 font-semibold mb-8">{i18n.t(`${messageKey}.title`)}</h2>
        <p className="text-16 mb-16">{i18n.t(`${messageKey}.subtitle`)}</p>
        <button type="button" className="underline text-16" onClick={this.handleReload}>
          {i18n.t('errorBoundary.reload')}
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
